import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Lock, Clock, Search, Filter, TrendingUp, BookOpen, Zap } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Badge, Spinner } from "@/components/kit";

const CATS = [
  { value: "all", label: "Semua", icon: BookOpen, color: "slate" },
  { value: "sales", label: "Sales Tips", icon: TrendingUp, color: "sky" },
  { value: "case", label: "Studi Kasus", icon: Sparkles, color: "violet" },
  { value: "growth", label: "Growth Hack UMKM", icon: Zap, color: "emerald" },
];

export default function CommunityTips() {
  const [tips, setTips] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [plan, setPlan] = useState("free");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/community/tips").then((r) => {
      setTips(r.data.tips);
      setIsPaid(r.data.is_paid);
      setPlan(r.data.plan);
    }).catch((e) => toast.error(apiErr(e)));
  }, []);

  const filtered = useMemo(() => {
    if (!tips) return [];
    const qn = q.trim().toLowerCase();
    return tips.filter((t) => {
      if (cat !== "all" && t.category !== cat) return false;
      if (!qn) return true;
      return (t.title + " " + t.excerpt + " " + (t.tags || []).join(" ")).toLowerCase().includes(qn);
    });
  }, [tips, cat, q]);

  if (tips === null) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7" /></div>;

  return (
    <div className="space-y-6">
      {/* Hero — narrative, cinematic */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 text-white px-8 py-10 md:px-12 md:py-14">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-violet-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-sky-500/20 rounded-full blur-3xl" />
        <div className="relative">
          <Badge color="violet" className="bg-violet-500/20 text-violet-200 border-0">
            <Sparkles className="w-3 h-3" /> Community Tips · {tips.length} artikel
          </Badge>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold mt-4 leading-tight max-w-2xl">
            Wisdom dari ribuan kampanye nyata.
          </h1>
          <p className="text-slate-300 mt-3 max-w-xl text-base md:text-lg leading-relaxed">
            Ringkasan strategi, studi kasus, dan growth hack untuk freelancer & UMKM Indonesia —
            dirakit dari hasil <b className="text-white">RESADA users</b> yang sudah closing miliaran rupiah.
          </p>
          {!isPaid && (
            <div className="mt-5 inline-flex items-center gap-2 text-xs bg-amber-500/15 border border-amber-400/30 text-amber-200 rounded-xl px-3.5 py-2" data-testid="community-paywall-notice">
              <Lock className="w-4 h-4" />
              <span>Kamu di paket <b>Free</b> — hanya 1 artikel terbuka. Upgrade ke <b>Starter (Rp 99rb/bulan)</b> untuk akses semua.</span>
            </div>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            data-testid="community-search"
            type="text"
            placeholder="Cari tips, judul, tag..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-xl border border-slate-200 pl-10 pr-3 py-2 text-sm focus:border-sky-300 outline-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {CATS.map((c) => {
            const Ic = c.icon;
            const active = cat === c.value;
            return (
              <button key={c.value} data-testid={`community-cat-${c.value}`} onClick={() => setCat(c.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl transition border ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
                <Ic className="w-3.5 h-3.5" /> {c.label}
              </button>
            );
          })}
        </div>
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} artikel</span>
      </div>

      {/* Cards grid */}
      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-400">Tidak ada artikel cocok.</Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="community-grid">
          {filtered.map((t) => <TipCard key={t.slug} tip={t} />)}
        </div>
      )}
    </div>
  );
}

function TipCard({ tip }) {
  const isLocked = tip.locked;
  const catMeta = CATS.find((c) => c.value === tip.category) || CATS[0];
  return (
    <Link to={`/app/community/${tip.slug}`}
      data-testid={`community-card-${tip.slug}`}
      className="group block rounded-2xl overflow-hidden bg-white border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <img src={tip.cover_image} alt={tip.title} loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${isLocked ? "grayscale brightness-50" : ""}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <Badge color={catMeta.color}>{catMeta.label}</Badge>
          {tip.is_featured && <Badge color="amber">★ Featured</Badge>}
        </div>
        {isLocked && (
          <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm">
            <div className="text-center text-white">
              <Lock className="w-10 h-10 mx-auto mb-2 opacity-80" />
              <p className="text-xs uppercase tracking-widest">Starter+ only</p>
            </div>
          </div>
        )}
        <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-xs text-white/90 bg-black/30 backdrop-blur rounded-full px-2.5 py-1">
          <Clock className="w-3 h-3" /> {tip.read_time} min
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-lg text-slate-800 line-clamp-2 group-hover:text-sky-600 transition">{tip.title}</h3>
        <p className="text-sm text-slate-500 mt-2 line-clamp-3 leading-relaxed">{tip.excerpt}</p>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-50">
          <div className="flex flex-wrap gap-1">
            {(tip.tags || []).slice(0, 2).map((tag) => (
              <span key={tag} className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">#{tag}</span>
            ))}
          </div>
          {isLocked ? <Lock className="w-4 h-4 text-amber-500" /> : <span className="text-xs text-sky-600 font-semibold">Baca →</span>}
        </div>
      </div>
    </Link>
  );
}
